import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { TrashIcon, PlusCircleIcon, ArchiveBoxIcon } from '@heroicons/react/24/outline';
import { doc, getDoc, updateDoc, collection, getDocs, query, where, getFirestore, serverTimestamp, addDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import JobSearch from '../../JobSearch';
import JobActivityFeed from '../../features/dashboard/jobs/JobActivityFeed';
import FastTrackActions from '../../features/dashboard/jobs/FastTrackActions';
import { addRecommendedJobToUser, removeRecommendedJobFromUser, archiveJobForUser } from '../../../services/firebase';
import JobFormModal from '../JobFormModal';

export default function UserJobsTab({ user }) {
  const [newJobId, setNewJobId] = useState('');
  const [isAddingJob, setIsAddingJob] = useState(false);
  const [recommendedJobs, setRecommendedJobs] = useState([]);
  const [recommendedJobDetails, setRecommendedJobDetails] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [userAddedJobs, setUserAddedJobs] = useState([]);
  const [userLikedJobs, setUserLikedJobs] = useState([]);
  const [isLoadingJobs, setIsLoadingJobs] = useState(false);
  const [jobReactions, setJobReactions] = useState({});
  const [recommendJob, setRecommendJob] = useState({});
  // New state variables for JobFormModal
  const [isJobFormModalOpen, setIsJobFormModalOpen] = useState(false);
  const [isCreatingJob, setIsCreatingJob] = useState(false);

  // Fetch user jobs and recommended jobs when component mounts
  useEffect(() => {
    if (user && user.id) {
      fetchUserJobs();
      fetchUserRecommendedJobs();
    }
  }, [user]);

  // Fetch recommended jobs details
  useEffect(() => {
    if (recommendedJobs && recommendedJobs.length > 0) {
      fetchRecommendedJobsDetails();
    }
  }, [recommendedJobs]);

  // Fetch liked jobs details
  useEffect(() => {
    if (userLikedJobs && userLikedJobs.length > 0) {
      fetchLikedJobsDetails();
    }
  }, [userLikedJobs]);

  const fetchUserJobs = async () => {
    try {
      setIsLoadingJobs(true);
      const db = getFirestore();
      const userProfileRef = doc(db, "user_profiles", user.id);
      const userProfileSnap = await getDoc(userProfileRef);
      
      if (userProfileSnap.exists()) {
        const userData = userProfileSnap.data();
        // Get user's own added jobs
        const addedJobs = userData.addedJobs || [];
        
        if (addedJobs.length > 0) {
          const jobDetails = await fetchJobDetailsByIds(addedJobs);
          setUserAddedJobs(jobDetails);
        }
      }
    } catch (error) {
      console.error('Error fetching user jobs:', error);
    } finally {
      setIsLoadingJobs(false);
    }
  };

  const fetchJobDetailsByIds = async (jobIds) => {
    if (!jobIds.length) return [];
    
    try {
      const db = getFirestore();
      const jobsRef = collection(db, "jobs");
      const jobsData = [];
      
      // Process in batches to avoid potential limitations
      for (const jobId of jobIds) {
        const jobQuery = query(jobsRef, where("id", "==", jobId));
        const querySnapshot = await getDocs(jobQuery);
        
        querySnapshot.forEach((doc) => {
          jobsData.push({
            id: doc.id,
            ...doc.data()
          });
        });
      }
      
      return jobsData;
    } catch (error) {
      console.error("Error fetching job details:", error);
      return [];
    }
  };

  // Enhance fetchJobDetails to get recommendation details
  const fetchJobDetails = async (jobId) => {
    try {
      const db = getFirestore();
      const jobRef = doc(db, "jobs", jobId);
      const jobSnap = await getDoc(jobRef);
      
      if (jobSnap.exists()) {
        const jobData = jobSnap.data();
        const userReactions = jobData.userReactions || {};
        
        // Check if this user has reacted to this job
        const userReaction = userReactions[user.id] || null;
        
        // Get user profile for recommendJob data
        const userProfileRef = doc(db, "user_profiles", user.id);
        const userProfileSnap = await getDoc(userProfileRef);
        let recommendData = null;
        
        if (userProfileSnap.exists()) {
          const userData = userProfileSnap.data();
          // Get recommendation data from the recommendJob map
          recommendData = userData.recommendJob && userData.recommendJob[jobId] 
            ? userData.recommendJob[jobId] 
            : null;
          
          // Log the data for debugging
          console.log(`Job ${jobId} recommendation data:`, recommendData);
        }
        
        return {
          id: jobSnap.id,
          ...jobSnap.data(),
          userReaction, // Include the user reaction data
          recommendData // Include the recommendation data
        };
      } else {
        console.log(`Job with ID ${jobId} not found`);
        return null;
      }
    } catch (error) {
      console.error("Error fetching job details:", error);
      return null;
    }
  };

  const fetchUserRecommendedJobs = async () => {
    try {
      const db = getFirestore();
      const userProfileRef = doc(db, "user_profiles", user.id);
      const userProfileSnap = await getDoc(userProfileRef);
      
      if (userProfileSnap.exists()) {
        const userData = userProfileSnap.data();
        // Get recommended jobs from the recommendJob map
        const recommendJobMap = userData.recommendJob || {};
        setRecommendJob(recommendJobMap);
        // Extract job IDs from the recommendJob map
        const recommendedJobIds = Object.keys(recommendJobMap);
        setRecommendedJobs(recommendedJobIds);
        // Set liked jobs from profile data
        setUserLikedJobs(userData.likedJobs || []);
        // Set job reactions if available
        setJobReactions(userData.jobReactions || {});
      }
    } catch (error) {
      console.error('Error fetching user recommended jobs:', error);
    }
  };

  const fetchRecommendedJobsDetails = async () => {
    try {
      const jobDetails = [];
      for (const jobId of recommendedJobs) {
        const jobDetail = await fetchJobDetails(jobId);
        if (jobDetail) {
          jobDetails.push(jobDetail);
        }
      }
      setRecommendedJobDetails(jobDetails);
    } catch (error) {
      console.error("Error fetching recommended job details:", error);
    }
  };

  const fetchLikedJobsDetails = async () => {
    try {
      const jobDetails = await fetchJobDetailsByIds(userLikedJobs);
      setUserLikedJobs(jobDetails);
    } catch (error) {
      console.error("Error fetching liked job details:", error);
    }
  };

  const handleJobSelect = (job) => {
    if (job && job.id) {
      console.log("Selected job to recommend:", job);
      handleAddJobRecommendation({ preventDefault: () => {} }, job.id);
    }
  };

  // Handler for creating a new job and recommending it to the user
  const handleCreateAndRecommendJob = async (jobData) => {
    try {
      setIsCreatingJob(true);
      const db = getFirestore();
      const jobsRef = collection(db, "jobs");
      
      // Add the job to Firestore
      const newJobRef = await addDoc(jobsRef, {
        ...jobData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      // Get the job ID
      const newJobId = newJobRef.id;
      
      // Update the job with its ID (for easier querying)
      await updateDoc(newJobRef, {
        id: newJobId
      });
      
      toast.success('Job created successfully!');
      
      // Recommend the job to the user
      await handleAddJobRecommendation({ preventDefault: () => {} }, newJobId);
      
      // Close the modal
      setIsJobFormModalOpen(false);
      
      // Refresh the jobs list
      await fetchUserRecommendedJobs();
      
    } catch (error) {
      console.error('Error creating and recommending job:', error);
      toast.error(`Error: ${error.message}`);
    } finally {
      setIsCreatingJob(false);
    }
  };

  const handleAddJobRecommendation = async (e, jobId = null) => {
    e.preventDefault();
    const idToAdd = jobId || newJobId.trim();
    
    if (!idToAdd) return;
    
    setIsAddingJob(true);
    try {
      console.log(`Adding job recommendation: ${idToAdd} for user: ${user.id}`);
      // Get the current admin's user ID from auth
      const auth = getAuth();
      const adminId = auth.currentUser ? auth.currentUser.uid : null;
      const adminEmail = auth.currentUser ? auth.currentUser.email : 'Unknown admin';
      
      // Get user name
      const userName = user.displayName || user.email || 'Unknown user';
      
      const result = await addRecommendedJobToUser(user.id, idToAdd, adminId, adminEmail, userName);
      
      if (result.success) {
        if (result.alreadyExists) {
          toast.info(`Job was already recommended to ${userName}`);
        } else {
          toast.success(`Job recommendation added for ${userName} by ${adminEmail}`);
          // Update local state with the new recommendJob map
          setRecommendJob(result.recommendJob);
          // Update recommendedJobs array from the keys of recommendJob
          setRecommendedJobs(Object.keys(result.recommendJob));
        }
        setNewJobId('');
      } else {
        toast.error(`Failed to add job recommendation: ${result.error}`);
      }
    } catch (error) {
      console.error('Error adding job recommendation:', error);
      toast.error(`Error: ${error.message}`);
    } finally {
      setIsAddingJob(false);
    }
  };

  const handleRemoveJobRecommendation = async (jobId) => {
    try {
      // Get admin info
      const auth = getAuth();
      const adminEmail = auth.currentUser ? auth.currentUser.email : 'Unknown admin';
      
      // Get user name
      const userName = user.displayName || user.email || 'Unknown user';
      
      const result = await removeRecommendedJobFromUser(user.id, jobId);
      
      if (result.success) {
        toast.success(`Job recommendation removed for ${userName} by ${adminEmail}`);
        // Update local state with the new recommendJob map
        setRecommendJob(result.recommendJob);
        // Update recommendedJobs array from the keys of recommendJob
        setRecommendedJobs(Object.keys(result.recommendJob));
      } else {
        toast.error(`Failed to remove job recommendation: ${result.error}`);
      }
    } catch (error) {
      console.error('Error removing job recommendation:', error);
      toast.error(`Error: ${error.message}`);
    }
  };

  // New function to archive a job recommendation
  const handleArchiveJobForUser = async (jobId) => {
    try {
      // Get the current admin's user ID from auth
      const auth = getAuth();
      const adminId = auth.currentUser ? auth.currentUser.uid : null;
      const adminEmail = auth.currentUser ? auth.currentUser.email : 'Unknown admin';
      
      // Include user name in the archive action
      const userName = user.displayName || user.email || 'Unknown user';
      
      const result = await archiveJobForUser(user.id, jobId, adminId, adminEmail, userName);
      
      if (result.success) {
        toast.success(`Job archived successfully for ${userName}`);
        // Update local state with the new recommendJob map
        setRecommendJob(result.recommendJob);
        // Keep the job ID in the array so we still show it in admin view
        // but now it will have archived status
        await fetchUserRecommendedJobs(); // Refresh the job list to show updated status
      } else {
        toast.error(`Failed to archive job: ${result.error}`);
      }
    } catch (error) {
      console.error('Error archiving job:', error);
      toast.error(`Error: ${error.message}`);
    }
  };

  // Helper to generate activities feed data from job data
  const getJobActivities = (job) => {
    const activities = [];
    
    // Add recommendation event
    if (job.recommendData?.recommendedAt) {
      activities.push({
        type: 'recommended',
        adminName: job.recommendData.adminEmail || 'Admin',
        userName: job.recommendData.userName || user.displayName || user.email || 'User',
        timestamp: job.recommendData.recommendedAt,
      });
    }
    
    // Add archive event if archived
    if (job.recommendData?.status === 'archived' && job.recommendData.archivedAt) {
      activities.push({
        type: 'archived',
        adminName: job.recommendData.archivedByEmail || 'Admin',
        userName: job.recommendData.userName || user.displayName || user.email || 'User',
        timestamp: job.recommendData.archivedAt,
      });
    }
    
    // Add reaction event if user reacted to job
    if (job.recommendData?.reaction) {
      activities.push({
        type: job.recommendData.reaction === 'like' ? 'liked' : 'disliked',
        userName: job.recommendData.userName || user.displayName || user.email || 'User',
        timestamp: job.recommendData.reactionTimestamp || job.recommendData.timestamp,
      });
    }
    
    // Add recruiter contact event if admin contacted recruiter
    if (job.userReaction?.adminContactedRecruiter && job.userReaction.adminContactedRecruiterAt) {
      activities.push({
        type: 'contacted_recruiter',
        adminName: job.userReaction.adminContactedRecruiterBy || 'Admin',
        userName: user.displayName || user.email || 'User',
        timestamp: job.userReaction.adminContactedRecruiterAt,
        notes: job.userReaction.adminNotes
      });
    }
    
    // Add fast track activities if present
    if (job.userReaction?.activities) {
      job.userReaction.activities.forEach(activity => {
        if (activity && (activity.type === 'fast_track_connect' || activity.type === 'fast_track_invite')) {
          activities.push({
            ...activity,
            userName: user.displayName || user.email || 'User'
          });
        }
      });
    }
    
    // Sort activities by timestamp, most recent first
    return activities.sort((a, b) => {
      const timeA = a.timestamp?.seconds || 0;
      const timeB = b.timestamp?.seconds || 0;
      return timeB - timeA;
    });
  };

  if (!user) return null;

  return (
    <div className="space-y-6">
      {/* Recommended Jobs Section */}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-gray-100">Recommended Jobs</h3>
            <button
              type="button"
              onClick={() => setIsJobFormModalOpen(true)}
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-700 dark:hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:focus:ring-indigo-400"
            >
              <PlusCircleIcon className="h-4 w-4 mr-1" /> Create & Recommend Job
            </button>
          </div>
          
          {/* Job Search using the new component */}
          <div className="mb-4">
            <label htmlFor="job-search" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Search for jobs to recommend
            </label>
            <JobSearch 
              onSelect={handleJobSelect}
              placeholder="Search by job title..."
            />
          </div>
          
          {/* List of current job recommendations */}
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Current Recommendations</h4>
          {recommendedJobs && recommendedJobs.length > 0 ? (
            <ul className="divide-y divide-gray-200 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
              {recommendedJobDetails.length > 0 ? (
                recommendedJobDetails.map((job) => (
                  <li key={job.id} className={`px-4 py-3 flex flex-col w-full ${job.recommendData?.status === 'archived' ? 'bg-gray-50 dark:bg-gray-800' : 'bg-white dark:bg-gray-900'}`}>
                    <div className="flex flex-col w-full">
                      <div className="flex justify-between items-start w-full">
                        <div className="flex-1">
                          <div className="flex items-center mb-1">
                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate max-w-prose">
                              {job.title || "Untitled Job"}
                            </p>
                            {job.recommendData?.status === 'archived' && (
                              <span className="ml-2 inline-flex items-center rounded-md bg-gray-50 dark:bg-gray-700 px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-300 ring-1 ring-inset ring-gray-500/10 dark:ring-gray-400/20">
                                Archived
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {job.company || "Unknown company"}
                            {job.location && ` • ${job.location}`}
                          </p>
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">ID: {job.id}</p>
                        </div>
                        
                        <div className="flex">
                          {job.recommendData?.status !== 'archived' ? (
                            <button
                              type="button"
                              onClick={() => handleArchiveJobForUser(job.id)}
                              className="ml-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
                              title="Archive job (hide from user but keep in database)"
                            >
                              <ArchiveBoxIcon className="h-4 w-4" />
                              <span className="sr-only">Archive job recommendation</span>
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleAddJobRecommendation({ preventDefault: () => {} }, job.id)}
                              className="ml-2 text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300"
                              title="Restore job (make visible to user again)"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
                              </svg>
                              <span className="sr-only">Restore job recommendation</span>
                            </button>
                          )}
                        </div>
                      </div>
                        
                      {/* Job Activity Feed */}
                      <div className="mt-3 border-t border-gray-100 dark:border-gray-700 pt-3 w-full">
                        <JobActivityFeed 
                          activities={getJobActivities(job)}
                        />
                        
                        {/* Admin engagement buttons */}
                        {job.userReaction && job.userReaction.reaction === 'like' && (
                          <div className="mt-3 pt-2 border-t border-gray-100 dark:border-gray-700">
                            <button 
                              type="button"
                              className={`text-xs px-2 py-1.5 rounded ${
                                job.userReaction.adminContactedRecruiter ? 'bg-green-100 text-green-700 border border-green-200' : 'bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100'
                              }`}
                              style={{ display: 'none' }}
                              onClick={async () => {
                                try {
                                  const db = getFirestore();
                                  const jobRef = doc(db, "jobs", job.id);
                                  
                                  // Get the current admin's email
                                  const auth = getAuth();
                                  const adminEmail = auth.currentUser ? auth.currentUser.email : 'Unknown admin';
                                  
                                  // Update the job document
                                  await updateDoc(jobRef, {
                                    [`userReactions.${user.id}.adminContactedRecruiter`]: !job.userReaction.adminContactedRecruiter,
                                    [`userReactions.${user.id}.adminContactedRecruiterAt`]: job.userReaction.adminContactedRecruiter ? null : serverTimestamp(),
                                    [`userReactions.${user.id}.adminContactedRecruiterBy`]: job.userReaction.adminContactedRecruiter ? null : adminEmail
                                  });
                                  
                                  // Refresh job details
                                  fetchRecommendedJobsDetails();
                                  
                                  // Show toast notification
                                  const userName = user.displayName || user.email || 'Unknown user';
                                  if (!job.userReaction.adminContactedRecruiter) {
                                    toast.success(`Marked recruiter as contacted for ${userName} by ${adminEmail}`);
                                  } else {
                                    toast.success(`Unmarked recruiter contact for ${userName} by ${adminEmail}`);
                                  }
                                } catch (error) {
                                  console.error("Error updating admin engagement:", error);
                                  toast.error(`Error: ${error.message}`);
                                }
                              }}
                            >
                              {job.userReaction.adminContactedRecruiter ? (
                                <>
                                  <span className="flex items-center">
                                    <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    Recruiter contacted
                                  </span>
                                </>
                              ) : (
                                <span className="flex items-center">
                                  <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                  </svg>
                                  Mark recruiter contacted
                                </span>
                              )}
                            </button>
                            
                            {/* Fast Track Actions */}
                            <FastTrackActions 
                              jobId={job.id} 
                              userId={user.id} 
                              onActionComplete={() => fetchRecommendedJobsDetails()}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </li>
                ))
              ) : (
                recommendedJobs.map((jobId) => (
                  <li key={jobId} className="px-4 py-3 flex items-center justify-between bg-white dark:bg-gray-900">
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100 break-all">
                      {jobId} <span className="text-xs text-gray-500 dark:text-gray-400">(Loading details...)</span>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Recommended for {user.displayName || user.email || 'Unknown user'}
                      </div>
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemoveJobRecommendation(jobId)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <TrashIcon className="h-4 w-4" />
                      <span className="sr-only">Remove job recommendation</span>
                    </button>
                  </li>
                ))
              )}
            </ul>
          ) : (
            <p className="text-sm text-gray-500 italic">No recommended jobs yet.</p>
          )}
        </div>
      </div>
      
      {/* User Added Jobs */}
      <div className="bg-gray-50 rounded-lg border border-gray-200">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">User Added Jobs</h3>
          
          {isLoadingJobs ? (
            <div className="text-center py-4">
              <svg className="animate-spin h-5 w-5 text-indigo-500 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
          ) : userAddedJobs.length > 0 ? (
            <ul className="divide-y divide-gray-200 border border-gray-200 rounded-md overflow-hidden">
              {userAddedJobs.map((job) => (
                <li key={job.id} className="px-4 py-3 bg-white">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{job.title}</p>
                      <p className="text-xs text-gray-500">{job.company} • {job.location}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleAddJobRecommendation({ preventDefault: () => {} }, job.id)}
                      className="ml-2 inline-flex items-center text-xs font-medium text-indigo-600 hover:text-indigo-500"
                      disabled={recommendedJobs.includes(job.id)}
                    >
                      {recommendedJobs.includes(job.id) ? (
                        <span className="text-gray-400">Already Recommended</span>
                      ) : (
                        <>
                          <PlusCircleIcon className="h-4 w-4 mr-1" />
                          Recommend
                        </>
                      )}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-500 italic">User hasn't added any jobs.</p>
          )}
        </div>
      </div>
      
      {/* User Liked Jobs */}
      <div className="bg-gray-50 rounded-lg border border-gray-200">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">User Liked Jobs</h3>
          
          {isLoadingJobs ? (
            <div className="text-center py-4">
              <svg className="animate-spin h-5 w-5 text-indigo-500 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
          ) : userLikedJobs.length > 0 ? (
            <ul className="divide-y divide-gray-200 border border-gray-200 rounded-md overflow-hidden">
              {userLikedJobs.map((job) => (
                <li key={job.id} className="px-4 py-3 bg-white">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{job.title}</p>
                      <p className="text-xs text-gray-500">{job.company} • {job.location}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleAddJobRecommendation({ preventDefault: () => {} }, job.id)}
                      className="ml-2 inline-flex items-center text-xs font-medium text-indigo-600 hover:text-indigo-500"
                      disabled={recommendedJobs.includes(job.id)}
                    >
                      {recommendedJobs.includes(job.id) ? (
                        <span className="text-gray-400">Already Recommended</span>
                      ) : (
                        <>
                          <PlusCircleIcon className="h-4 w-4 mr-1" />
                          Recommend
                        </>
                      )}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400 italic">User hasn't liked any jobs.</p>
          )}
        </div>
      </div>
      
      {/* JobFormModal */}
      <JobFormModal
        isOpen={isJobFormModalOpen}
        onClose={() => setIsJobFormModalOpen(false)}
        onSave={handleCreateAndRecommendJob}
        isLoading={isCreatingJob}
      />
    </div>
  );
} 